/**
 * ParallaxRenderer — Reusable parallax layer manager for PixiJS v8.
 *
 * Manages multiple scrolling layers at different speeds for
 * a parallax depth effect. Supports seamless tiling and day/night tinting.
 *
 * @module ParallaxRenderer
 */

import { Container, Graphics } from 'pixi.js';

/**
 * @typedef {Object} ParallaxLayer
 * @property {string} name
 * @property {number} speed - Scroll speed multiplier (0 = static, 1 = full speed)
 * @property {Container} container
 * @property {number} width - Width of a single tile
 * @property {boolean} tiled - Whether the layer tiles seamlessly
 */

class ParallaxRenderer {
  constructor() {
    /** @type {ParallaxLayer[]} */
    this._layers = [];

    /** Root container for all parallax layers. */
    this.root = new Container();

    /** Tint overlay graphics for day/night. */
    this._tintOverlay = new Graphics();
    this._tintOverlay.alpha = 0;

    /** Current viewport dimensions. */
    this._viewWidth = 800;
    this._viewHeight = 600;
  }

  /**
   * Register a parallax layer.
   * @param {string} name - Layer identifier
   * @param {number} speed - Scroll speed multiplier (0 = static, 0.2 = slow, 1 = full)
   * @param {Container} container - PixiJS container with the layer's graphics
   * @param {object} [options]
   * @param {number} [options.width] - Width of a single tile (for seamless scrolling)
   * @param {boolean} [options.tiled=true] - Whether to tile the layer
   */
  addLayer(name, speed, container, options = {}) {
    const width = options.width || this._viewWidth;
    const tiled = options.tiled !== undefined ? options.tiled : true;

    /** @type {ParallaxLayer} */
    const layer = {
      name,
      speed,
      container,
      width,
      tiled,
    };

    this._layers.push(layer);
    this.root.addChild(container);

    // Add the tint overlay on top of all layers (re-add to stay on top)
    if (this._tintOverlay.parent) {
      this.root.removeChild(this._tintOverlay);
    }
    this.root.addChild(this._tintOverlay);
  }

  /**
   * Update all layer positions based on camera X position.
   * Handles seamless tiling by wrapping positions.
   * @param {number} cameraX - Horizontal camera offset in world units
   */
  update(cameraX) {
    for (const layer of this._layers) {
      const offset = -cameraX * layer.speed;

      if (layer.tiled && layer.width > 0) {
        // Wrap position for seamless scrolling
        const wrapped = ((offset % layer.width) + layer.width) % layer.width;
        layer.container.x = -wrapped;
      } else {
        layer.container.x = offset;
      }
    }
  }

  /**
   * Apply a day/night tint overlay to the entire parallax view.
   * @param {number} color - Hex color (e.g., 0x112244)
   * @param {number} alpha - Opacity (0-1)
   */
  setTint(color, alpha) {
    this._tintOverlay.clear();
    if (alpha > 0) {
      this._tintOverlay.rect(0, 0, this._viewWidth, this._viewHeight);
      this._tintOverlay.fill({ color, alpha: 1 });
      this._tintOverlay.alpha = alpha;
    } else {
      this._tintOverlay.alpha = 0;
    }
  }

  /**
   * Smoothly interpolate toward a target tint.
   * @param {number} targetColor
   * @param {number} targetAlpha
   * @param {number} lerpSpeed - 0-1, how fast to approach target per second
   * @param {number} dt - Delta time in seconds
   */
  lerpTint(targetColor, targetAlpha, lerpSpeed, dt) {
    const t = Math.min(1, lerpSpeed * dt);
    const currentAlpha = this._tintOverlay.alpha;
    const newAlpha = currentAlpha + (targetAlpha - currentAlpha) * t;

    this._tintOverlay.clear();
    if (newAlpha > 0.001) {
      this._tintOverlay.rect(0, 0, this._viewWidth, this._viewHeight);
      this._tintOverlay.fill({ color: targetColor, alpha: 1 });
      this._tintOverlay.alpha = newAlpha;
    } else {
      this._tintOverlay.alpha = 0;
    }
  }

  /**
   * Update viewport dimensions. Called on resize.
   * @param {number} width
   * @param {number} height
   */
  resize(width, height) {
    this._viewWidth = width;
    this._viewHeight = height;

    // Update tint overlay size
    const alpha = this._tintOverlay.alpha;
    if (alpha > 0) {
      this._tintOverlay.clear();
      this._tintOverlay.rect(0, 0, width, height);
      this._tintOverlay.fill({ color: 0x000000, alpha: 1 });
      this._tintOverlay.alpha = alpha;
    }
  }

  /**
   * Get a layer by name.
   * @param {string} name
   * @returns {ParallaxLayer|null}
   */
  getLayer(name) {
    return this._layers.find(l => l.name === name) || null;
  }

  /**
   * Remove all layers and clean up.
   */
  destroy() {
    for (const layer of this._layers) {
      if (layer.container.parent) {
        layer.container.parent.removeChild(layer.container);
      }
      layer.container.destroy({ children: true });
    }
    this._layers = [];

    if (this._tintOverlay.parent) {
      this._tintOverlay.parent.removeChild(this._tintOverlay);
    }
    this._tintOverlay.destroy();

    if (this.root.parent) {
      this.root.parent.removeChild(this.root);
    }
    this.root.destroy({ children: true });
  }
}

export default ParallaxRenderer;
